// /src/EditQuotation.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "./services/api";
import generatePDF from "./PDFGenerator";
import SafeListboxPortal from "./SafeListbox"; 
import UnitConverter from "./UnitConverter"; 

// Helper: Format date for input field
const toLocalDateInputValue = (dateLike) => {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// 🔹 FIXED: Rupee formatting now handles Strings ("2500") and Numbers (2500)
const rupee = (v) => {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(n)
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 0 })
    : "0";
};

const EditQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State Management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  
  // Data State
  const [categories, setCategories] = useState([]);
  
  const [customer, setCustomer] = useState({
    name: "",
    city: "",
    mobile: "",
    date: new Date().toISOString().split("T")[0],
  });

  const [discountAmount, setDiscountAmount] = useState(0);
  const [includeGst, setIncludeGst] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [customItems, setCustomItems] = useState([]);

  // 🔹 INITIALIZATION: Load Categories AND Quotation Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // 1. Fetch Categories and Quotation in parallel
        const [categoriesRes, quotationRes] = await Promise.all([
          api.get(`/quotations/items/categories`),
          api.get(`/quotations/${id}`)
        ]);

        const categoriesData = categoriesRes.data;
        const quotationData = quotationRes.data;

        if (!quotationData) {
          throw new Error("Quotation not found");
        }

        // 2. Set Categories into state
        setCategories(categoriesData || []);

        // 3. Prefill Form with both sets of data
        prefillForm(quotationData, categoriesData || []);

      } catch (err) {
        console.error("Error initializing edit page:", err);
        // Optional: redirect on critical error
        // navigate("/quotations");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 🔹 PREFILL LOGIC
  const prefillForm = (q, loadedCategories) => {
    if (!q) return;

    // 1. Setup Customer
    setCustomer((prev) => ({
      ...prev,
      name: q.customer_name || q.customer?.name || "",
      city: q.city || q.customer?.city || "",
      mobile: q.mobile || q.customer?.mobile || "",
      date: q.date ? toLocalDateInputValue(q.date) : prev.date,
    }));

    // Priority: discount_amount -> discount_percent calc -> 0
    let loadedDiscount = 0;
    if (q.discount_amount !== undefined && q.discount_amount !== null) {
        loadedDiscount = parseFloat(q.discount_amount);
    } else if (q.discount_percent) {
        // Legacy support: calculate amount from percent
        const total = parseFloat(q.total || 0);
        loadedDiscount = (total * parseFloat(q.discount_percent)) / 100;
    }
    setDiscountAmount(Number(loadedDiscount || 0));

    // Prefill GST flag
    if (q.include_gst !== undefined && q.include_gst !== null) {
        setIncludeGst(!!q.include_gst);
    } else {
        setIncludeGst(false);
    }

    // 2. Prepare empty fixed slots based on loaded categories
    const fixed = loadedCategories.map(() => ({ name: "", price: 0, quantity: "" }));
    const customs = [];
    
    // Get array of items from DB
    const itemsArray = Array.isArray(q.items) ? q.items : [];

    // 3. Map items to slots
    itemsArray.forEach((it) => {
      // Find which category index this item belongs to
      const catIndex = loadedCategories.findIndex(
        (c) => c.category === it.category
      );

      if (catIndex >= 0) {
        // It's a fixed category item
        fixed[catIndex] = {
          name: it.name || "",
          price: Number(it.price || 0),
          quantity: String(it.quantity || ""),
          description: it.description || "",
        };
      } else {
        // It's a custom item or category mismatch
        customs.push({
          name: it.name || "",
          price: String(it.price || ""),
          quantity: String(it.quantity || ""),
          description: it.description || "",
        });
      }
    });

    setSelectedItems(fixed);
    setCustomItems(customs);
  };

  // Handlers
  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      setCustomer({ ...customer, [name]: value.replace(/[^0-9]/g, "") });
    } else {
      setCustomer({ ...customer, [name]: value });
    }
  };

  const handleSelect = (index, selectedName) => {
    // If cleared
    if (!selectedName) {
      setSelectedItems((prev) => {
        const next = [...prev];
        next[index] = { name: "", price: 0, quantity: "", description: "" };
        return next;
      });
      return;
    }

    // Find details from category state
    const categoryData = categories[index];
    const itemDetails = categoryData?.items.find((i) => i.name === selectedName);

    setSelectedItems((prev) => {
      const next = [...prev];
      next[index] = {
        name: itemDetails?.name || "",
        price: itemDetails?.price || 0,
        quantity: next[index].quantity || "", // keep qty if user is just switching model
        description: next[index].description || "", 
      };
      return next;
    });
  };

  const handleClearItem = (index) => {
    setSelectedItems((prev) => {
      const next = [...prev];
      next[index] = { name: "", price: 0, quantity: "", description: "" };
      return next;
    });
  };

  const handleQuantityChange = (index, event) => {
    const quantity = event.target.value.replace(/[^0-9]/g, "");
    setSelectedItems((prev) => {
      const next = [...prev];
      if (!next[index]) next[index] = { name: "", price: 0, quantity: "", description: "" };
      next[index].quantity = quantity;
      return next;
    });
  };

  const handleDescriptionChange = (index, event) => {
    const description = event.target.value;
    setSelectedItems((prev) => {
      const next = [...prev];
      if (!next[index]) next[index] = { name: "", price: 0, quantity: "", description: "" };
      next[index].description = description;
      return next;
    });
  };

  // Custom Items Handlers
  const addCustomItem = () => {
    setCustomItems((prev) => [...prev, { name: "", price: "", quantity: "", description: "" }]);
  };

  const updateCustomItem = (idx, field, value) => {
    setCustomItems((prev) => {
      const next = [...prev];
      if (field === "price") {
        next[idx][field] = value.replace(/[^0-9.]/g, "");
      } else if (field === "quantity") {
        next[idx][field] = value.replace(/[^0-9]/g, "");
      } else {
        next[idx][field] = value;
      }
      return next;
    });
  };

  const removeCustomItem = (idx) => {
    setCustomItems((prev) => prev.filter((_, i) => i !== idx));
  };

  // Calculations
  const buildItemsPayload = () => {
    // Dynamic category names from state
    const categoryNames = categories.map((c) => c.category);

    const fixed = selectedItems
      .map((item, index) =>
        item.quantity && parseInt(item.quantity, 10) > 0 && item.name
          ? {
              category: categoryNames[index],
              name: item.name,
              quantity: parseInt(item.quantity, 10),
              price: Number(item.price || 0),
              total: Number(item.price || 0) * parseInt(item.quantity || 0, 10),
              description: item.description || "",
            }
          : null
      )
      .filter(Boolean);

    const customs = customItems
      .map((ci) => {
        const qty = parseInt(ci.quantity || "0", 10);
        const price = parseFloat(ci.price || "0");
        if (!ci.name?.trim() || qty <= 0 || price <= 0) return null;
        return {
          category: "Custom",
          name: ci.name.trim(),
          quantity: qty,
          price: price,
          total: qty * price,
          description: ci.description || "",
        };
      })
      .filter(Boolean);

    return [...fixed, ...customs];
  };

  const fixedTotal = selectedItems.reduce(
    (sum, item) =>
      sum + (item?.quantity ? item.price * parseInt(item.quantity, 10) : 0),
    0
  );
  
  const customTotal = customItems.reduce((sum, item) => {
    const qty = parseInt(item.quantity || "0", 10);
    const price = parseFloat(item.price || "0");
    return sum + (qty > 0 && price > 0 ? qty * price : 0);
  }, 0);

  const total = fixedTotal + customTotal;
  const finalDiscount = parseFloat(discountAmount || 0);
  const grandTotal = Math.max(0, total - finalDiscount);

  const isCustomerValid =
    customer.name.trim() !== "" &&
    customer.city.trim() !== "" &&
    customer.mobile.trim() !== "";

  // Update API Call
  const updateQuotation = async () => {
    if (!isCustomerValid) return;
    setSaving(true);
    setError(false);
    setSaved(false);

    try {
      const payload = {
        customer: { ...customer },
        items: buildItemsPayload(),
        discountAmount: finalDiscount,
        discount_amount: finalDiscount,
        total: parseFloat(total.toFixed(2)),
        netTotal: parseFloat(grandTotal.toFixed(2)),
        includeGst,
      };

      await api.put(`/quotations/update/${id}`, payload);

      setSaving(false);
      setSaved(true);
      setError(false);

      setTimeout(() => navigate("/quotations"), 1500);
    } catch (err) {
      console.error("Update error:", err);
      setSaving(false);
      setError(true);
      setSaved(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-white pb-20">
      {/* Background Decor */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="orb absolute w-[500px] h-[500px] -top-20 -left-20 bg-purple-600/20 rounded-full blur-[100px]"></div>
        <div className="orb absolute w-[400px] h-[400px] bottom-0 right-0 bg-indigo-600/20 rounded-full blur-[100px]" style={{ animationDelay: "-5s" }}></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8 relative z-10 max-w-7xl">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-200 via-pink-200 to-indigo-200 inline-block">
            Edit Quotation
          </h1>
          <p className="text-purple-300/60 mt-2 text-sm uppercase tracking-widest font-medium">Updating Quotation #{id || "..."}</p>
        </div>

        <div className="glass-panel p-6 md:p-8 rounded-3xl border border-white/10 shadow-2xl backdrop-blur-xl bg-white/5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-purple-500/30 border-t-purple-400 rounded-full animate-spin"></div>
              <p className="mt-4 text-purple-300 text-sm font-medium animate-pulse">Loading quotation data...</p>
            </div>
          ) : (
            <>
              {/* Customer Details Section */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-8">
                <div className="md:col-span-4">
                     <label className="block text-xs font-bold text-purple-300 uppercase tracking-wider mb-1 ml-1">Customer Name</label>
                     <input
                        name="name"
                        placeholder="Enter full name"
                        value={customer.name}
                        onChange={handleCustomerChange}
                        className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all font-medium"
                     />
                </div>
                <div className="md:col-span-3">
                     <label className="block text-xs font-bold text-purple-300 uppercase tracking-wider mb-1 ml-1">City</label>
                     <input
                        name="city"
                        placeholder="City / Location"
                        value={customer.city}
                        onChange={handleCustomerChange}
                        className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                     />
                </div>
                <div className="md:col-span-3">
                     <label className="block text-xs font-bold text-purple-300 uppercase tracking-wider mb-1 ml-1">Mobile</label>
                     <input
                        name="mobile"
                        placeholder="10-digit number"
                        value={customer.mobile}
                        onChange={handleCustomerChange}
                        className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all font-mono"
                     />
                </div>
                <div className="md:col-span-2">
                     <label className="block text-xs font-bold text-purple-300 uppercase tracking-wider mb-1 ml-1">Date</label>
                     <input
                        name="date"
                        type="date"
                        value={customer.date}
                        onChange={handleCustomerChange}
                        className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all cursor-pointer"
                     />
                </div>
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent my-8"></div>

              {/* Dynamic Categories Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
                {categories.map((category, i) => {
                  return (
                    <div
                      key={category.category + i}
                      className="group relative bg-slate-900/40 rounded-2xl p-5 border border-white/5 hover:border-purple-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-purple-900/10"
                    >
                      <h3 className="text-lg font-bold text-purple-100 mb-4 flex items-center justify-between">
                        {category.category}
                        {selectedItems[i]?.quantity > 0 && (
                          <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full border border-emerald-500/20 font-bold">
                              Added
                          </span>
                        )}
                      </h3>

                      <div className="flex flex-col gap-4">
                        <div className="relative z-20">
                            <SafeListboxPortal
                              items={category.items}
                              value={selectedItems[i]?.name || ""}
                              onChange={(val) => handleSelect(i, val)}
                              placeholder="Choose model..."
                              buttonClassName="w-full bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-left text-sm text-white transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                              optionsClassName="max-h-60 overflow-y-auto bg-slate-900 border border-white/10 rounded-xl shadow-2xl py-1"
                              renderLabel={(item) => item.name}
                              renderMeta={(item) => `₹${rupee(includeGst ? item.price / 1.18 : item.price)}`}
                            />
                        </div>

                        {/* Description Input */}
                        <div className="relative z-10">
                            <input
                                type="text"
                                value={selectedItems[i]?.description || ""}
                                onChange={(e) => handleDescriptionChange(i, e)}
                                placeholder="Add description (optional)..."
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500/40 placeholder-white/30"
                                disabled={!selectedItems[i]?.name}
                            />
                        </div>

                         {/* Qty & Price Row */}
                        <div className="flex items-center gap-3">
                           <div className="relative w-24">
                              <input
                                  type="text"
                                  inputMode="numeric"
                                  value={selectedItems[i]?.quantity || ""}
                                  onChange={(e) => handleQuantityChange(i, e)}
                                  placeholder="Qty"
                                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-center text-white font-bold focus:outline-none focus:ring-2 focus:ring-purple-500/40 placeholder-white/20"
                                  disabled={!selectedItems[i]?.name}
                              />
                               <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white/30 uppercase font-bold pointer-events-none">
                                    NOS
                                </span>
                           </div>
                           
                            <div className="flex-1 bg-gradient-to-r from-emerald-900/20 to-teal-900/20 border border-emerald-500/20 rounded-xl px-4 py-3 flex items-center justify-between">
                                <span className="text-xs text-emerald-400/70 font-bold uppercase tracking-wider">Total</span>
                                <span className="text-lg font-bold text-emerald-400 font-mono">
                                    ₹{selectedItems[i]?.quantity
                                        ? rupee((includeGst ? selectedItems[i].price / 1.18 : selectedItems[i].price) * parseInt(selectedItems[i].quantity || 0, 10))
                                        : "0"}
                                </span>
                           </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Custom Items Section */}
              <div className="mb-8 bg-slate-900/30 rounded-2xl p-6 border border-white/5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-purple-200 font-bold flex items-center gap-2">
                    <i className="fas fa-tools text-purple-400"></i> Custom / Ad-hoc Items
                  </h3>
                  <button
                    type="button"
                    onClick={addCustomItem}
                    className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm font-bold border border-white/10 transition-all flex items-center gap-2"
                  >
                    <i className="fas fa-plus"></i> Add Item
                  </button>
                </div>

                {customItems.length === 0 ? (
                  <div className="text-center py-6 text-white/20 italic text-sm border-2 border-dashed border-white/5 rounded-xl">
                    No custom items added. Click above to add extra services or parts.
                  </div>
                ) : (
                    <div className="space-y-3">
                        {customItems.map((ci, idx) => (
                        <div
                            key={idx}
                            className="grid grid-cols-12 gap-3 items-center bg-white/5 p-3 rounded-xl border border-white/5 animate-[fadeIn_0.3s_ease-out]"
                        >
                            <div className="col-span-12 md:col-span-5 flex flex-col gap-2">
                                <input
                                type="text"
                                placeholder="Item Name"
                                value={ci.name}
                                onChange={(e) => updateCustomItem(idx, "name", e.target.value)}
                                className="w-full bg-transparent border-b border-white/10 px-2 py-1 text-white placeholder-white/20 focus:outline-none focus:border-purple-400 transition-colors"
                                />
                                <input
                                type="text"
                                placeholder="Add description (optional)..."
                                value={ci.description || ""}
                                onChange={(e) => updateCustomItem(idx, "description", e.target.value)}
                                className="w-full bg-transparent border-b border-white/10 px-2 py-1 text-sm text-white/70 placeholder-white/20 focus:outline-none focus:border-purple-400 transition-colors"
                                />
                            </div>
                            <div className="col-span-3 md:col-span-2">
                                <input
                                type="text"
                                placeholder="Qty"
                                value={ci.quantity}
                                onChange={(e) => updateCustomItem(idx, "quantity", e.target.value)}
                                className="w-full bg-transparent border-b border-white/10 px-2 py-1 text-center text-white placeholder-white/20 focus:outline-none focus:border-purple-400 transition-colors"
                                />
                            </div>
                            <div className="col-span-4 md:col-span-2">
                                <input
                                type="text"
                                placeholder="Price"
                                value={ci.price}
                                onChange={(e) => updateCustomItem(idx, "price", e.target.value)}
                                className="w-full bg-transparent border-b border-white/10 px-2 py-1 text-center text-white placeholder-white/20 focus:outline-none focus:border-purple-400 transition-colors"
                                />
                            </div>
                            <div className="col-span-4 md:col-span-2 text-right font-mono text-emerald-400 font-bold text-sm md:text-base">
                                ₹{rupee(((includeGst ? parseFloat(ci.price || 0) / 1.18 : parseFloat(ci.price || 0)) * parseInt(ci.quantity || 0, 10)) || 0)}
                            </div>
                            <div className="col-span-1 text-right">
                                <button
                                    onClick={() => removeCustomItem(idx)}
                                    className="text-white/40 hover:text-red-400 transition-colors"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                        ))}
                    </div>
                )}
              </div>

              <UnitConverter />
              
               <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent my-8"></div>


              {/* Total Section */}
              <div className="flex justify-end mb-4 pr-2">
                <label className="flex items-center cursor-pointer gap-3 bg-slate-900/50 px-4 py-2 rounded-xl border border-white/10 hover:bg-slate-900/80 transition-colors">
                  <span className="text-sm font-bold text-purple-300 uppercase tracking-widest">
                    Bill With GST
                  </span>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      className="sr-only" 
                      checked={includeGst}
                      onChange={(e) => setIncludeGst(e.target.checked)}
                    />
                    <div className={`block w-10 h-6 rounded-full transition-colors ${includeGst ? 'bg-emerald-500' : 'bg-slate-700'}`}></div>
                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${includeGst ? 'transform translate-x-4' : ''}`}></div>
                  </div>
                </label>
              </div>
              
              <div className="bg-gradient-to-r from-slate-900 to-slate-900 border border-white/10 rounded-3xl p-8 relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 blur-[80px] rounded-full pointer-events-none"></div>
                 
                 <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                     
                     {/* Discount Input */}
                     <div className="flex flex-col gap-2">
                         <label className="text-sm font-bold text-white/50 uppercase tracking-widest">Discount Amount (₹)</label>
                         <div className="relative">
                            <input
                                type="number"
                                value={discountAmount}
                                onChange={(e) => setDiscountAmount(e.target.value)}
                                placeholder="0"
                                className="w-40 bg-white/5 border border-white/10 rounded-xl pl-8 pr-4 py-3 text-xl font-bold text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all text-right"
                            />
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 font-bold">₹</span>
                         </div>
                     </div>

                     {/* Final Total */}
                     <div className="text-center md:text-right">
                         {includeGst ? (
                             <div className="flex flex-col items-end gap-1 mb-4 text-sm font-mono">
                                 <div className="flex justify-between w-52 text-white/70">
                                     <span>Taxable Amount:</span>
                                     <span>₹{rupee(grandTotal / 1.18)}</span>
                                 </div>
                                 <div className="flex justify-between w-52 text-white/70">
                                     <span>CGST (9%):</span>
                                     <span>₹{rupee((grandTotal - (grandTotal / 1.18)) / 2)}</span>
                                 </div>
                                 <div className="flex justify-between w-52 text-white/70">
                                     <span>SGST (9%):</span>
                                     <span>₹{rupee((grandTotal - (grandTotal / 1.18)) / 2)}</span>
                                 </div>
                             </div>
                         ) : (
                             <div className="flex flex-col items-end gap-1 mb-4 text-sm font-mono">
                                 <div className="flex justify-between w-52 text-white/70">
                                     <span>Total Amount:</span>
                                     <span>₹{rupee(total)}</span>
                                 </div>
                                 {finalDiscount > 0 && (
                                     <div className="flex justify-between w-52 text-white/70 text-red-400">
                                         <span>Discount:</span>
                                         <span>- ₹{rupee(finalDiscount)}</span>
                                     </div>
                                 )}
                             </div>
                         )}
                         <div className="text-sm font-bold text-emerald-400 uppercase tracking-widest mb-1">Net Payable Amount</div>
                         <div className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                            ₹{rupee(Math.round(grandTotal))}
                         </div>
                         <div className="text-white/30 text-sm mt-2 font-medium">
                            Total Items: {buildItemsPayload().length}
                         </div>
                     </div>
                 </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
                <button
                   onClick={() =>
                    generatePDF({
                      customer,
                      selectedItems,
                      categoryNames: categories.map((c) => c.category),
                      customItems,
                      total,
                      discountAmount: finalDiscount,
                      grandTotal,
                      includeGst,
                    })
                  }
                  disabled={!isCustomerValid}
                  className={`group flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-bold text-lg shadow-lg border border-white/10 transition-all ${
                    !isCustomerValid 
                    ? "bg-white/5 text-white/30 cursor-not-allowed" 
                    : "bg-white/10 hover:bg-white/20 text-white hover:scale-[1.02]"
                  }`}
                >
                  <i className="fas fa-file-pdf text-purple-400 group-hover:text-purple-300"></i> 
                  Download PDF
                </button>

                <button
                  onClick={() =>
                    window.open(
                      generatePDF({
                        customer,
                        selectedItems,
                        categoryNames: categories.map((c) => c.category),
                        customItems,
                        total,
                        discountAmount: finalDiscount,
                        grandTotal,
                        includeGst,
                      }, "preview"),
                      "_blank"
                    )
                  }
                  disabled={!isCustomerValid}
                  className={`group flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-bold text-lg shadow-lg border border-white/10 transition-all ${
                    !isCustomerValid
                    ? "bg-white/5 text-white/30 cursor-not-allowed"
                    : "bg-white/10 hover:bg-white/20 text-white hover:scale-[1.02]"
                  }`}
                >
                  <i className="fas fa-eye text-cyan-400 group-hover:text-cyan-300"></i>
                  Preview
                </button>

                 <button
                  onClick={updateQuotation}
                  disabled={!isCustomerValid || saving || saved}
                  className={`flex items-center justify-center gap-3 px-12 py-4 rounded-xl font-bold text-lg shadow-lg shadow-indigo-900/30 transition-all ${
                     !isCustomerValid || saving || saved
                     ? "bg-indigo-600/50 text-white/50 cursor-not-allowed"
                     : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white hover:scale-[1.02]"
                  }`}
                >
                  {saving ? (
                       <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : saved ? (
                       <i className="fas fa-check"></i>
                  ) : (
                       <i className="fas fa-save"></i>
                  )}
                  {saving ? "Updating..." : saved ? "Updated" : "Update Quotation"}
                </button>

                <button
                  onClick={() => navigate("/quotations")}
                  className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-white/50 hover:text-white hover:bg-white/5 transition-all"
                >
                  Cancel
                </button>
              </div>

              {saved && (
                <div className="mt-6 p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-xl text-center animate-pulse">
                   <p className="text-emerald-300 font-bold">✅ Quotation updated successfully. Redirecting...</p>
                </div>
              )}
              {error && (
                <div className="mt-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-center">
                   <p className="text-red-200 font-bold">❌ Failed to update quotation. Please try again.</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="text-center mt-12 mb-8">
            
        </div>
      </div>

       {/* Global & Animation Styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .orb {
          animation: float 10s ease-in-out infinite;
        }
        @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
        }
        
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        ::-webkit-scrollbar-track {
          background: transparent; 
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1); 
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2); 
        }
      `}</style>
    </div>
  );
};

export default EditQuotation;